import { useLoadScript } from "@react-google-maps/api";
import { useEffect, useRef, useState } from "react";
import { parseGoogleAddressComponents } from "@/components/organisms/MyFavourites/FavouriteLocationsTable/suggestions";
import { useSiteConfig } from "@/hooks";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "@/lib/googleMaps";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { useGetGuestBookingDataQuery, useGetGuestQuoteByIdQuery } from "@/redux/apis/guest/guestApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { fillLocationAddressParts, restoreBookingDraft } from "@/redux/slices/clientBooking/clientBookingSlice";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";
import { hydrateDraftFromGuestQuote } from "./hydrateDraftFromGuestQuote";

export type QuotePrefillStatus = "loading" | "resolved" | "not-found";

interface UseHydrateBookingFromQuoteOptions {
  /** The `quote_uuid` from the widget handoff. Absent → nothing to resolve → "not-found". */
  quoteUuid?: string;
  /**
   * Resolve the operator FROM the quote's `provider_code` (public `verifyProvider` → `setActiveOperator`).
   * The guest flow and the authenticated client self-service flow both need this — the quote carries the
   * operator. The operator OBO flow already has the operator fixed by the `:operatorId` route, so it passes
   * `false` and the hook only hydrates the draft.
   */
  resolveOperator: boolean;
  /**
   * Skip hydrating the draft from the quote — used when the caller restores a stashed draft instead
   * (a guest returning from an ACCOUNT_EXISTS 409 → login). Operator resolution still runs.
   */
  skipHydration?: boolean;
}

/**
 * Rehydrate the shared booking draft from a widget quote handoff (`?quote_uuid=…`).
 *
 * Lifted out of `GuestBooking` so all three entry points can prefill from a quote: the unauthenticated
 * guest flow, the authenticated client self-service flow, and the operator OBO flow. The quote-fetch and
 * the pure `hydrateDraftFromGuestQuote` mapper are operator-agnostic; the only difference between callers
 * is whether the operator is resolved from the quote (guest / client) or already fixed by the route (OBO).
 *
 * The guest quote + job-data endpoints are public, so they work identically whether or not there's a
 * session — no authenticated twin is needed for prefill.
 */
export const useHydrateBookingFromQuote = ({
  quoteUuid,
  resolveOperator,
  skipHydration = false,
}: UseHydrateBookingFromQuoteOptions): { status: QuotePrefillStatus } => {
  const dispatch = useAppDispatch();
  const { apiUrl } = useSiteConfig();
  const [verify] = useLazyVerifyProviderQuery();
  const providerUuid = useAppSelector(operatorIdSelector);

  // When the operator is already fixed by the route (OBO) there's nothing to resolve, so start resolved.
  const [status, setStatus] = useState<QuotePrefillStatus>(resolveOperator ? "loading" : "resolved");

  // Public, operator-independent read: the quote carries the operator (provider_code) and the inputs to
  // rehydrate the draft.
  const { data: quoteData, isError: isQuoteError } = useGetGuestQuoteByIdQuery(quoteUuid ?? "", { skip: !quoteUuid });
  const { data: quoteBookingData, isError: isJobDataError } = useGetGuestBookingDataQuery(providerUuid ?? "", {
    skip: !providerUuid,
  });

  // Resolve the operator FROM the quote's provider_code, then store it active. No quote / bad quote /
  // a quote with no operator → not-available.
  useEffect(() => {
    if (!resolveOperator) return;
    if (!quoteUuid || isQuoteError || (quoteData && !quoteData.provider_code)) {
      setStatus("not-found");
      return;
    }
    const code = quoteData?.provider_code;
    if (!code) return; // still loading the quote
    let cancelled = false;
    verify({ providerCode: code, apiUrl })
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        dispatch(setActiveOperator(res));
        setStatus("resolved");
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("(useHydrateBookingFromQuote) Error verifying provider:", { code, error: e });
        setStatus("not-found");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveOperator, quoteUuid, quoteData, isQuoteError, apiUrl]);

  // Hydrate the draft once from the quote. Wait for job-data (it enriches vehicle/transfer/extras) UNLESS
  // it errored — a job-data outage must not drop the whole prefill, so fall through with it undefined;
  // locations + datetime still restore.
  const hydratedRef = useRef(false);
  const [hydratedLocations, setHydratedLocations] = useState<PointsOfInterest[]>([]);
  useEffect(() => {
    if (skipHydration || hydratedRef.current || !quoteData) return;
    if (!quoteBookingData && !isJobDataError) return;
    const draft = hydrateDraftFromGuestQuote(quoteData.request, quoteBookingData);
    if (draft) {
      dispatch(restoreBookingDraft(draft));
      setHydratedLocations([draft.pickup, ...(draft.stops ?? []), draft.destination].filter((poi): poi is PointsOfInterest => !!poi));
    }
    hydratedRef.current = true;
  }, [skipHydration, quoteData, quoteBookingData, isJobDataError, dispatch]);

  // The quote's `city` is the widget's LGA and it carries no postcode, so reverse-geocode each hydrated
  // location for its suburb, postcode and codes once Maps loads (before or after hydration). The quoted
  // address and coordinates are what the quote priced, so they are never touched; a failed geocode keeps
  // the quote's values.
  const { isLoaded: isMapsLoaded } = useLoadScript(GOOGLE_MAPS_LOADER_OPTIONS);
  useEffect(() => {
    if (!isMapsLoaded || hydratedLocations.length === 0) return;
    let cancelled = false;
    const geocoder = new window.google.maps.Geocoder();
    hydratedLocations.forEach(({ address, lat, lng }) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (cancelled || status !== "OK" || !results?.[0]) return;
        const parts = parseGoogleAddressComponents(results[0].address_components);
        dispatch(
          fillLocationAddressParts({
            address,
            lat,
            lng,
            ...(parts.city && { city: parts.city }),
            ...(parts.postcode && { postcode: parts.postcode }),
            ...(parts.state_code && { state_code: parts.state_code }),
            ...(parts.country_code && { country_code: parts.country_code }),
          })
        );
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isMapsLoaded, hydratedLocations, dispatch]);

  return { status };
};
